/* OrderConfirmationPage - Displays order confirmation details after successful checkout
   Shows order number, customer details, ordered items, and delivery information
   Updated: Added Mollie payment status display with polling for payment updates
   Updated: Aligned layout with site-wide styling guide (ContactBar + Header, consistent padding)
   Updated: Restyled the missing-order state to match the new warm beige and forest-green public design.
   Updated: Uses get_order_by_number SECURITY DEFINER function so the order can be loaded
   after the Mollie redirect even when the cart session has been cleared.
   Security update: customer contact details are no longer read from the public API
   (they are not publicly readable anymore) but taken from the checkout navigation
   state, and only shown when available.
   Security update: the stored payment link is only rendered when it is a valid
   https URL on the payment provider's own domain.
   Updated: Shows the single chosen delivery day (old orders keep their 3-day period).
   Updated: Added DataFast goals: order_placed fires once on load, order_completed
   fires when payment_status becomes 'paid'.
   Updated: Added Google Analytics gtag 'purchase' event with transaction_id,
   value, currency, and item details — fires once when order data is loaded.
   Updated: Improved GA4 purchase event — fires only when order + items are loaded
   and payment is confirmed; uses subtotal minus coupon_discount for value,
   sends shipping separately; omits tax (hardcoded 0 in DB, prices include BTW);
   sends coupon code at event level; allocates discount proportionally across
   items as per-unit discount; uses product_variant_id/product_id as item_id;
   safe number parsing; dev-only debug logging; duplicate protection via
   useRef + stable transaction_id.
   Updated: purchase is sent explicitly to GA4 property 555216009 via send_to: GA4_MEASUREMENT_ID. */

// Safely parse a monetary string into a finite number, returning 0 on failure.
function safeNum(value: string | number | null | undefined): number {
  const n = typeof value === 'number' ? value : parseFloat(String(value ?? ''));
  return Number.isFinite(n) ? n : 0;
}

import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactBar from '../components/ContactBar';
import PageBreadcrumb from '@/components/PageBreadcrumb';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CircleCheck as CheckCircle2, Package, Truck, Calendar, Clock, MapPin, Mail, Phone, User, Loader as Loader2, CreditCard, Clock as Hourglass, CircleAlert as AlertCircle, ArrowRight, SearchX } from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { trackGoal } from '@/lib/datafast';
import { GA4_MEASUREMENT_ID } from '@/lib/analytics';
import { formatDeliveryDay } from '@/lib/delivery-date';

interface CustomerDetails {
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  customer_address?: string;
  customer_city?: string;
  customer_postal_code?: string;
  customer_notes?: string;
}

const ALLOWED_CHECKOUT_HOSTS = ['mollie.com', 'www.mollie.com'];

function safeCheckoutUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:') return null;
    const host = url.hostname.toLowerCase();
    const allowed = ALLOWED_CHECKOUT_HOSTS.some(
      (h) => host === h || host.endsWith(`.${h}`),
    );
    return allowed ? url.toString() : null;
  } catch {
    return null;
  }
}

interface Order {
  id: string;
  order_number: string;
  subtotal: string;
  tax: string;
  delivery_fee: string;
  coupon_discount: string;
  total: string;
  status: string;
  payment_method: string;
  payment_status: string | null;
  mollie_checkout_url: string | null;
  coupon_code: string | null;
  delivery_type: 'delivery' | 'pickup';
  delivery_week: string | null;
  pickup_timeslot: string | null;
  created_at: string;
  customer_name: string | null;
  customer_email: string | null;
  customer_phone: string | null;
  customer_address: string | null;
  customer_city: string | null;
  customer_postal_code: string | null;
  customer_notes: string | null;
}

interface OrderItem {
  id: string;
  product_name: string;
  variant_name: string | null;
  quantity: number;
  unit_price: string;
  total_price: string;
  product_variant_id: string | null;
  product_id: string | null;
}

export default function OrderConfirmationPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const orderNumber = searchParams.get('order');
  const [order, setOrder] = useState<Order | null>(null);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Customer details come from navigation state (direct checkout flow) or
  // from the database (when returning from Mollie redirect). DB fields take
  // priority since they are always present; nav state is a fallback.
  const customer: CustomerDetails = {
    customer_name: order?.customer_name || (location.state as { customer?: CustomerDetails } | null)?.customer?.customer_name,
    customer_email: order?.customer_email || (location.state as { customer?: CustomerDetails } | null)?.customer?.customer_email,
    customer_phone: order?.customer_phone || (location.state as { customer?: CustomerDetails } | null)?.customer?.customer_phone,
    customer_address: order?.customer_address || (location.state as { customer?: CustomerDetails } | null)?.customer?.customer_address,
    customer_city: order?.customer_city || (location.state as { customer?: CustomerDetails } | null)?.customer?.customer_city,
    customer_postal_code: order?.customer_postal_code || (location.state as { customer?: CustomerDetails } | null)?.customer?.customer_postal_code,
    customer_notes: order?.customer_notes || (location.state as { customer?: CustomerDetails } | null)?.customer?.customer_notes,
  };

  const refreshOrder = useCallback(async () => {
    if (!orderNumber) return;
    // Actively sync the payment status from Mollie before re-reading the order,
    // so the page reflects the real payment state even if the webhook hasn't fired yet.
    try {
      await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-mollie-payment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ order_number: orderNumber }),
      });
    } catch { /* sync failure is non-fatal — fall through to DB read */ }
    const { data } = await supabase.rpc('get_order_by_number', { p_order_number: orderNumber });
    if (data && data.length > 0) setOrder(data[0] as unknown as Order);
  }, [orderNumber]);

  useEffect(() => {
    if (!orderNumber) {
      setError('Geen bestelnummer gevonden');
      setLoading(false);
      return;
    }

    const fetchOrder = async () => {
      try {
        // Sync payment status from Mollie first so the page reflects the real state.
        try {
          await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-mollie-payment`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ order_number: orderNumber }),
          });
        } catch { /* sync failure is non-fatal */ }

        const { data: orderData, error: orderError } = await supabase.rpc('get_order_by_number', { p_order_number: orderNumber });

        if (orderError) throw orderError;
        if (!orderData || orderData.length === 0) throw new Error('Bestelling niet gevonden');

        const orderRow = orderData[0] as unknown as Order;
        setOrder(orderRow);

        const { data: itemsData, error: itemsError } = await supabase.rpc('get_order_items_by_number', { p_order_number: orderNumber });

        if (itemsError) throw itemsError;

        setOrderItems((itemsData || []) as unknown as OrderItem[]);
      } catch (err) {
        console.error('Error fetching order:', err);
        setError('Kon bestelling niet laden. Controleer uw bestelnummer.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderNumber]);

  useEffect(() => {
    if (!order) return;
    trackGoal('order_placed', { order_number: order.order_number, total: parseFloat(order.total) });
  }, [order]);

  // Fire GA4 purchase event only when order + items are loaded and payment is confirmed.
  // Uses useRef to prevent duplicate firing across React rerenders.
  // The stable transaction_id (order_number) also lets GA4 deduplicate server-side.
  const purchaseFired = useRef(false);
  useEffect(() => {
    // Guard: wait for both order and items to be loaded
    if (!order || orderItems.length === 0 || purchaseFired.current) return;

    // Guard: only fire for completed orders.
    // - Mollie: payment_status must be 'paid'
    // - Bank transfer: status is 'pending' but the order is legitimately placed
    const isPaid = order.payment_status === 'paid';
    const isBankTransferConfirmed =
      order.payment_method === 'bank_transfer' &&
      (order.status === 'pending' || order.status === 'confirmed' || order.status === 'processing');
    if (!isPaid && !isBankTransferConfirmed) return;

    const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
    if (typeof gtag !== 'function') return;

    // Build the GA4 purchase payload.
    //
    // Data model (from DB triggers):
    //   subtotal = SUM(item.unit_price * item.quantity) — BEFORE coupon discount
    //   coupon_discount = discount amount (separate column)
    //   delivery_fee = shipping cost
    //   tax = hardcoded 0 (prices include BTW but tax is not split out in the DB)
    //   total = subtotal + delivery_fee - coupon_discount
    //
    // GA4 value must represent actual merchandise revenue (after discount, excl. shipping).
    // We allocate the coupon discount proportionally across items so that:
    //   value ≈ SUM(item.price * item.quantity)
    const subtotal = safeNum(order.subtotal);
    const couponDiscount = safeNum(order.coupon_discount);
    const shipping = safeNum(order.delivery_fee);
    const merchandiseValue = Math.max(0, subtotal - couponDiscount);

    // Validate required value — do not send the event if it's invalid
    if (!Number.isFinite(merchandiseValue) || merchandiseValue < 0) {
      if (import.meta.env.DEV) {
        console.warn('[GA4] purchase NOT sent — invalid merchandise value', {
          subtotal,
          coupon_discount: couponDiscount,
          merchandise_value: merchandiseValue,
        });
      }
      return;
    }

    // Allocate coupon discount proportionally across items.
    // GA4 item-level `discount` is PER UNIT, and `price` is the discounted price PER UNIT.
    // We keep full floating-point precision (no premature rounding) so that
    // SUM(item.price * item.quantity) ≈ event-level value.
    const itemsTotal = orderItems.reduce((sum, item) => sum + safeNum(item.unit_price) * item.quantity, 0);
    const items = orderItems.map((item) => {
      const unitPrice = safeNum(item.unit_price);
      const lineTotal = unitPrice * item.quantity;
      // Proportional line discount: (lineTotal / itemsTotal) * couponDiscount
      const lineDiscount = itemsTotal > 0 ? (lineTotal / itemsTotal) * couponDiscount : 0;
      // Per-unit discount for GA4 (discount is a unit-level parameter)
      const perUnitDiscount = item.quantity > 0 ? lineDiscount / item.quantity : 0;
      const effectiveUnitPrice = Math.max(0, unitPrice - perUnitDiscount);

      // Use stable catalogue identifier as item_id:
      // - Variant products: product_variant_id
      // - Simple products: product_id
      // - Fallback: order_items.id (per-order-line UUID, not ideal but stable within this order)
      const itemId = item.product_variant_id || item.product_id || item.id;

      const ga4Item: Record<string, unknown> = {
        item_id: itemId,
        item_name: item.product_name,
        quantity: item.quantity,
        price: effectiveUnitPrice,
      };

      if (item.variant_name) {
        ga4Item.item_variant = item.variant_name;
      }

      // GA4 item-scoped `discount` = per-unit discount amount
      if (perUnitDiscount > 0) {
        ga4Item.discount = perUnitDiscount;
      }

      return ga4Item;
    });

    const payload: Record<string, unknown> = {
      send_to: GA4_MEASUREMENT_ID,
      transaction_id: String(order.order_number),
      value: merchandiseValue,
      currency: 'EUR',
      items,
    };

    // Send shipping only when it is a positive finite number
    if (shipping > 0) {
      payload.shipping = shipping;
    }

    // tax is hardcoded to 0 in the database (prices include BTW but the split is not stored).
    // Omitting tax rather than sending 0, since 0 would be misleading in GA4 reports.

    // Send coupon code at event level when a coupon was used
    if (order.coupon_code) {
      payload.coupon = order.coupon_code;
    }

    // Mark as fired BEFORE calling gtag so that even if gtag throws,
    // we don't retry (a thrown gtag is a runtime issue, not a data issue).
    purchaseFired.current = true;

    gtag('event', 'purchase', payload);

    // Development-only debug log
    if (import.meta.env.DEV) {
      const itemsRevenue = items.reduce((s, i) => s + (i.price as number) * (i.quantity as number), 0);
      console.log('[GA4] purchase sent', {
        transaction_id: payload.transaction_id,
        value: payload.value,
        shipping: payload.shipping ?? 0,
        item_count: items.length,
        coupon: payload.coupon ?? null,
        items_revenue: Number(itemsRevenue.toFixed(4)),
        value_matches_items: Math.abs(itemsRevenue - merchandiseValue) < 0.01,
      });
    }
  }, [order, orderItems]);

  useEffect(() => {
    if (order?.payment_status === 'paid') {
      trackGoal('order_completed', { order_number: order.order_number, total: parseFloat(order.total) });
    }
  }, [order?.payment_status, order?.order_number, order?.total]);

  // Poll for payment status updates if payment is still pending
  useEffect(() => {
    if (!order || !order.payment_status || order.payment_status === 'paid' || order.payment_status === 'failed' || order.payment_status === 'cancelled' || order.payment_status === 'expired') {
      return;
    }
    if (order.payment_method === 'bank_transfer') return;
    // Mollie uses 'open' while the payment is in progress; keep polling until the webhook updates it.

    const interval = setInterval(refreshOrder, 5000);
    return () => clearInterval(interval);
  }, [order, refreshOrder]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Helmet>
          <title>Bestelling Laden... | Buitn Planten</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <ContactBar />
        <Header />
        <main className="pt-0 pb- px-4">
          <div className="min-h-[50vh] flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="h-12 w-12 animate-spin text-[#2F5233] mx-auto mb-4" />
              <p className="text-gray-600">Bestelling laden...</p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-[#fcfaf6] text-[#1a1a1a]">
        <Helmet>
          <title>Bestelling niet gevonden | Buitn Planten</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <ContactBar />
        <Header />
        <main className="px-6 py-16 sm:py-24">
          <div className="mx-auto flex min-h-[52vh] max-w-2xl items-center justify-center">
            <div className="w-full text-center">
              <div className="mx-auto mb-8 flex h-20 w-20 items-center justify-center rounded-full bg-[#e7e1d6] text-[#1e2b22] shadow-[0_12px_30px_rgba(30,43,34,0.08)]">
                <SearchX className="h-9 w-9" strokeWidth={1.7} />
              </div>
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.24em] text-[#5a6552]">
                Bestelling controleren
              </p>
              <h1 className="mx-auto max-w-xl text-4xl font-semibold leading-tight text-[#1e2b22] sm:text-5xl">
                We kunnen deze bestelling niet vinden
              </h1>
              <p className="mx-auto mt-6 max-w-lg text-base leading-7 text-[#5a6552] sm:text-lg">
                {error || 'Controleer of je via dezelfde browser terugkomt als waarmee je de bestelling hebt geplaatst.'}
              </p>
              <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row">
                <Button asChild className="min-h-12 rounded-md bg-[#1e2b22] px-7 text-white shadow-[0_10px_24px_rgba(30,43,34,0.16)] transition-all hover:-translate-y-0.5 hover:bg-[#5a6552]">
                  <Link to="/">
                    Terug naar home
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="min-h-12 rounded-md border-[#cfcac2] bg-transparent px-7 text-[#1e2b22] hover:bg-[#e7e1d6]">
                  <Link to="/assortiment">Bekijk assortiment</Link>
                </Button>
              </div>
              <p className="mt-8 text-sm text-[#7a6450]">
                Heb je wel een bestelling geplaatst? Neem dan gerust contact met ons op.
              </p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const formatPickupTimeslot = (timeslot: string) => {
    const [date, time] = timeslot.split(' ');
    const dateObj = new Date(date);
    return {
      date: format(dateObj, 'EEEE d MMMM yyyy', { locale: nl }),
      time: time
    };
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Bestelling bevestigd - {order.order_number} | Buitn Planten</title>
        <meta name="description" content={`Bedankt voor uw bestelling ${order.order_number}. U ontvangt spoedig een bevestigingsmail.`} />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <ContactBar />
      <Header />

      <main className="pt-8 pb-16 px-4">
        <div className="max-w-4xl mx-auto space-y-6">
          <PageBreadcrumb items={[{ label: 'Home', href: '/' }, { label: 'Bestelling bevestigd' }]} />
            {/* Payment Status Banner */}
            {order.payment_method === 'mollie' && order.payment_status && order.payment_status !== 'paid' && (
              <Card className={`border-2 ${
                (order.payment_status === 'awaiting_payment' || order.payment_status === 'open')
                  ? 'border-amber-300 bg-amber-50'
                  : 'border-red-300 bg-red-50'
              }`}>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    {(order.payment_status === 'awaiting_payment' || order.payment_status === 'open') ? (
                      <Hourglass className="h-10 w-10 text-amber-600 flex-shrink-0 animate-pulse" />
                    ) : (
                      <AlertCircle className="h-10 w-10 text-red-600 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <h2 className={`text-lg font-bold mb-1 ${
                        (order.payment_status === 'awaiting_payment' || order.payment_status === 'open') ? 'text-amber-900' : 'text-red-900'
                      }`}>
                        {(order.payment_status === 'awaiting_payment' || order.payment_status === 'open')
                          ? 'Betaling in afwachting'
                          : order.payment_status === 'failed'
                            ? 'Betaling mislukt'
                            : order.payment_status === 'cancelled'
                              ? 'Betaling geannuleerd'
                              : 'Betaling verlopen'}
                      </h2>
                      <p className={`text-sm mb-3 ${
                        (order.payment_status === 'awaiting_payment' || order.payment_status === 'open') ? 'text-amber-800' : 'text-red-800'
                      }`}>
                        {(order.payment_status === 'awaiting_payment' || order.payment_status === 'open')
                          ? 'Je betaling wordt verwerkt. Dit kan even duren.'
                          : order.payment_status === 'failed'
                            ? 'De betaling is niet gelukt. Probeer het opnieuw.'
                            : order.payment_status === 'cancelled'
                              ? 'De betaling is geannuleerd.'
                              : 'De betaallink is verlopen. Neem contact op of probeer opnieuw.'}
                      </p>
                      {safeCheckoutUrl(order.mollie_checkout_url) && (
                        <Button asChild>
                          <a href={safeCheckoutUrl(order.mollie_checkout_url) as string}>
                            <CreditCard className="h-4 w-4 mr-2" />
                            Nu betalen
                          </a>
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Success Message */}
            <Card className="border-green-200 bg-green-50">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="h-12 w-12 text-green-600 flex-shrink-0" />
                  <div className="flex-1">
                    <h1 className="text-2xl font-bold text-green-900 mb-2">
                      Bedankt voor bestellen bij Buitn Planten!
                    </h1>
                    <p className="text-green-800 mb-1">
                      {order.payment_status === 'paid'
                        ? 'Jouw bestelling is succesvol geplaatst, de betaling is ontvangen en wordt zo snel mogelijk voor jou verwerkt.'
                        : 'Jouw bestelling is succesvol geplaatst en wordt zo snel mogelijk verwerkt.'}
                    </p>
                    <p className="text-green-700 text-sm">
                      {customer.customer_email
                        ? <>Je ontvangt een bevestigingsmail op <strong>{customer.customer_email}</strong></>
                        : 'Je ontvangt een bevestigingsmail op het door jou opgegeven e-mailadres.'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Order Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Bestelling {order.order_number}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  {customer.customer_name && (
                    <div className="flex items-start gap-3">
                      <User className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Naam</p>
                        <p className="font-medium">{customer.customer_name}</p>
                      </div>
                    </div>
                  )}
                  {customer.customer_email && (
                    <div className="flex items-start gap-3">
                      <Mail className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="font-medium">{customer.customer_email}</p>
                      </div>
                    </div>
                  )}
                  {customer.customer_phone && (
                    <div className="flex items-start gap-3">
                      <Phone className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Telefoon</p>
                        <p className="font-medium">{customer.customer_phone}</p>
                      </div>
                    </div>
                  )}
                  {order.delivery_type === 'delivery' && customer.customer_address && (
                    <div className="flex items-start gap-3">
                      <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Afleveradres</p>
                        <p className="font-medium">{customer.customer_address}</p>
                        <p className="font-medium">{customer.customer_postal_code} {customer.customer_city}</p>
                      </div>
                    </div>
                  )}
                </div>

                {customer.customer_notes && (
                  <div className="pt-4 border-t">
                    <p className="text-sm text-gray-500 mb-1">Opmerkingen</p>
                    <p className="text-gray-700">{customer.customer_notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Delivery Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {order.delivery_type === 'delivery' ? (
                    <>
                      <Truck className="h-5 w-5" />
                      Bezorginformatie
                    </>
                  ) : (
                    <>
                      <MapPin className="h-5 w-5" />
                      Afhaalinformatie
                    </>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {order.delivery_type === 'delivery' && order.delivery_week ? (
                  <div className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Bezorgdag</p>
                      <p className="font-medium first-letter:uppercase">{formatDeliveryDay(order.delivery_week, order.created_at)}</p>
                    </div>
                  </div>
                ) : order.delivery_type === 'pickup' && order.pickup_timeslot ? (
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Calendar className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Afhaaldatum</p>
                        <p className="font-medium capitalize">{formatPickupTimeslot(order.pickup_timeslot).date}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Clock className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Tijdstip</p>
                        <p className="font-medium">{formatPickupTimeslot(order.pickup_timeslot).time}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Afhaaladres</p>
                        <p className="font-medium">Stegerensallee 11a</p>
                        <p className="font-medium">7701 PJ Dedemsvaart</p>
                      </div>
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>

            {/* Order Items */}
            <Card>
              <CardHeader>
                <CardTitle>Bestelde producten</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {orderItems.map((item) => (
                    <div key={item.id} className="flex justify-between items-start pb-4 border-b last:border-0 last:pb-0">
                      <div className="flex-1">
                        <p className="font-medium">{item.product_name}</p>
                        {item.variant_name && (
                          <p className="text-sm text-gray-600">{item.variant_name}</p>
                        )}
                        <p className="text-sm text-gray-500 mt-1">
                          Aantal: {item.quantity} × €{parseFloat(item.unit_price).toFixed(2)}
                        </p>
                      </div>
                      <p className="font-semibold text-lg">
                        €{parseFloat(item.total_price).toFixed(2)}
                      </p>
                    </div>
                  ))}

                  <div className="pt-4 space-y-2 border-t-2">
                    <div className="flex justify-between text-gray-600">
                      <span>Subtotaal</span>
                      <span>€{parseFloat(order.subtotal).toFixed(2)}</span>
                    </div>
                    {parseFloat(order.coupon_discount) > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>Kortingscode</span>
                        <span>-€{parseFloat(order.coupon_discount).toFixed(2)}</span>
                      </div>
                    )}
                    {parseFloat(order.delivery_fee) > 0 && (
                      <div className="flex justify-between text-gray-600">
                        <span>Bezorgkosten</span>
                        <span>€{parseFloat(order.delivery_fee).toFixed(2)}</span>
                      </div>
                    )}
                    {parseFloat(order.tax) > 0 && (
                      <div className="flex justify-between text-gray-600">
                        <span>BTW</span>
                        <span>€{parseFloat(order.tax).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-xl font-bold text-[#2F5233] pt-2">
                      <span>Totaal</span>
                      <span>€{parseFloat(order.total).toFixed(2)}</span>
                    </div>
                  </div>

                  {order.payment_method === 'bank_transfer' && (
                    <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="font-semibold text-blue-900 mb-2">Betaalinstructies</p>
                      <p className="text-sm text-blue-800">
                        Je ontvangt per e-mail een factuur met onze bankgegevens. Gelieve het bedrag over te maken
                        met vermelding van het bestelnummer <strong>{order.order_number}</strong>.
                      </p>
                    </div>
                  )}

                  {order.payment_method === 'mollie' && (order.payment_status === 'awaiting_payment' || order.payment_status === 'open') && order.mollie_checkout_url && (
                    <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="font-semibold text-amber-900 mb-2">Betaling open</p>
                      <p className="text-sm text-amber-800 mb-3">
                        Je hebt de betaling nog niet voltooid. Klik op de knop om alsnog te betalen.
                      </p>
                      <Button asChild>
                        <a href={order.mollie_checkout_url}>
                          <CreditCard className="h-4 w-4 mr-2" />
                          Nu betalen
                        </a>
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex gap-4 justify-center">
              <Button asChild variant="outline">
                <Link to="/">Terug naar home</Link>
              </Button>
              <Button asChild>
                <Link to="/assortiment">Verder winkelen</Link>
              </Button>
            </div>
          </div>
      </main>

      <Footer />
    </div>
  );
}
